import Layout from 'components/layout';

import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Box, Heading } from 'lucid-components-react';
import React from 'react';
import LazyLoad from 'react-lazyload';

import FooterAbTest from 'components/ab-tests/footer-swap/footer-swap';
import NavbarAbTest from 'components/ab-tests/nav-swap/nav-swap';
import CardTiles from 'components/card-tiles/card-tiles';
import FeatureList from 'components/feature-list/feature-list';
import FooterCard from 'components/footer-card/footer-card';
import Hero from 'components/hero/hero';
import LucidContainer from 'components/lucid-container/lucid-container';
import Quote from 'components/quote/quote';
import SignUpCta from 'components/sign-up-cta/sign-up-cta';
import {
  Drupal_FieldNodeSunriseFieldHeaderCta,
  Drupal_NodeSunrise,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphFullCard,
  Drupal_ParagraphHeroSection,
  Drupal_ParagraphPaperSection,
  Drupal_ParagraphQuote
} from 'generated/graphql';
import { useMenuDefaultFooter, useMenuMainNavLinks } from 'hooks/use-menu';
import { backgroundGrayPattern } from 'theme/shared';
import { LucidTheme, TemplateProps, isDefined } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';

const SunrisePage = ({ data, pageContext, location }: TemplateProps) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeSunrise;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const { langPrefix, pageType, nid, i18nKey, hideSsr } = pageContext;

  const i18n = i18nFunct(i18nData, i18nKey);

  const headerSection = node.fieldHeaderSection
    ? (node.fieldHeaderSection.entity as Drupal_ParagraphHeroSection)
    : null;

  const headerCtas = (node.fieldHeaderCta as Drupal_FieldNodeSunriseFieldHeaderCta[]).map(
    ({ entity }) => entity as Drupal_ParagraphCtaButton
  );

  const quote = node.fieldQuoteBundle
    ? (node.fieldQuoteBundle.entity as Drupal_ParagraphQuote)
    : null;

  const paperParagraphs = node
    .fieldPaperParagraph!.filter(isDefined)
    .map(({ entity }) => entity as Drupal_ParagraphPaperSection);

  const cards = node
    .fieldHowToSection!.filter(isDefined)
    .map(({ entity }) => entity as Drupal_ParagraphFullCard);

  const navbarMenu = useMenuMainNavLinks(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      trackers={{
        personas: node.fieldPersona,
        diagramTypes: node.fieldDiagramType,
        competitorTypes: node.fieldCompetitorType
      }}
    >
      {node.fieldHasNavbar && <NavbarAbTest menu={navbarMenu} />}

      <Box
        as="main"
        css={(theme: LucidTheme) => ({
          position: 'relative',
          zIndex: theme.zIndices[1],
          ...backgroundGrayPattern
        })}
      >
        {headerSection && (
          <Hero
            hasBackgroundImage
            backgroundImage={headerSection.fieldImage!}
            breadCrumbPage={node.fieldTitle!}
            buttons={headerCtas}
            header={headerSection.fieldHeader!}
            pageType={pageType}
            subheader={
              headerSection.fieldSubheader! || headerSection.fieldLongSubheader!
            }
            videoLink={node.fieldHeaderVideoLink!}
          />
        )}
        <LucidContainer>
          {paperParagraphs && <FeatureList list={paperParagraphs} />}
          {quote && quote.fieldQuote && <Quote quote={quote} />}
        </LucidContainer>
        {node.fieldFeaturedImage && (
          <LucidContainer>
            <Box
              css={(theme: LucidTheme) => ({
                borderRadius: theme.radii.card,
                boxShadow: theme.shadows[20]
              })}
              my={8}
            >
              <LazyLoad offset={20}>
                <img
                  alt="browserbar"
                  src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/consideration/basic-images/browser-bar@2x.png"
                />
              </LazyLoad>
              <LazyLoad offset={20}>
                <img
                  alt={node.fieldFeatureTitle!}
                  src={node.fieldFeaturedImage}
                />
              </LazyLoad>
            </Box>
          </LucidContainer>
        )}
        {cards.length > 0 && (
          <Box
            css={(theme: LucidTheme) => ({
              backgroundColor: theme.colors.coolGrey[100]
            })}
          >
            <LucidContainer>
              <Heading
                as="h2"
                color="white"
                pt={{ default: 5 }}
                variant="subheadS"
              >
                {node.fieldFeatureTitle ||
                  i18n('mktg-footer-resources-templates')}
              </Heading>
              <CardTiles tiles={cards} />
            </LucidContainer>
          </Box>
        )}
        {node.fieldHasFooterEmailOnly && (
          <Box
            css={theme => ({
              marginTop: `calc(${theme.space[10]}px + ${theme.space[7]}px)`,
              [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                marginTop: theme.space[5]
              }
            })}
          >
            <FooterCard>
              <Box
                className="card__title"
                css={(theme: LucidTheme) => ({
                  [`@media only screen and (max-width: ${theme.breakpoints.tablet})`]: {
                    paddingRight: theme.space[5]
                  }
                })}
              >
                <Heading as="h2" variant="subheadS">
                  {i18n('mktg_ct_consideration_footer_header')}
                </Heading>
              </Box>
              <Box
                className="card__cta"
                css={(theme: LucidTheme) => ({
                  flex: '0 1 45%',
                  maxWidth: '45%',
                  fontWeight: theme.fontWeights.bold,
                  [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                    flex: '0 1 100%',
                    maxWidth: '100%'
                  }
                })}
              >
                <SignUpCta />
              </Box>
            </FooterCard>
          </Box>
        )}
      </Box>

      {node.fieldHasFooter && (
        <FooterAbTest
          padFooter
          entityTranslations={entityTranslations}
          menu={footerMenu}
        />
      )}
    </Layout>
  );
};

export default SunrisePage;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeSunrise {
          ...entityFields
          fieldTitle
          fieldHeaderSection {
            entity {
              ... on Drupal_ParagraphHeroSection {
                fieldHeader
                fieldSubheader
                fieldLongSubheader
                fieldImage
              }
            }
          }
          fieldHeaderCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonLink
                fieldButtonText
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldPaperParagraph {
            entity {
              ... on Drupal_ParagraphPaperSection {
                fieldImage
                fieldHeader
                fieldDescriptionWysiwyg {
                  processed
                }
                fieldCta {
                  url {
                    path
                  }
                  title
                }
              }
            }
          }
          fieldQuoteBundle {
            entity {
              ... on Drupal_ParagraphQuote {
                fieldQuote
                fieldAttribution
              }
            }
          }
          fieldHowToSection {
            entity {
              ... on Drupal_ParagraphFullCard {
                fieldImage
                fieldCtaHeading
                fieldLink
                fieldCardSubheader {
                  processed
                }
              }
            }
          }
          fieldHasNavbar
          fieldHasFooter
          fieldHasFooterEmailOnly
          fieldHeaderVideoLink
          fieldUseEmailOnly
          fieldFeaturedImage
          fieldFeatureTitle
          fieldPersona {
            entity {
              entityLabel
            }
          }
          fieldDiagramType {
            entity {
              entityLabel
            }
          }
          fieldCompetitorType {
            entity {
              entityLabel
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_solutions_footer_cta_primary
            mktg_ct_solutions_footer_or_call
            mktg_ct_consideration_footer_header
            mktg_ct_all_request_a_demo
            mktg_ct_all_get_a_quote
            mktg_footer_resources_templates
          }
        }
      }
    }
  }
`;
